<template>
  <div
    class="flex flex-col w-full mt-8 border rounded-lg overflow-x-auto font-poppins border-stone-300"
    ref="el"
  >
    <div
      v-if="headers && headers.length"
      class="grid items-center h-12 pl-2.5 border-b border-gray-200 bg-gray-100 font-semibold text-sm text-gray-700 min-w-125"
      :style="{ gridTemplateColumns: columnWidths?.join(' ') || defaultColumnWidths.join(' ') }"
    >
      <div
        v-for="(header, index) in headers"
        :key="index"
        class="px-3 min-w-0 flex items-center h-full bg-gray-100"
        :class="isSortableHeaderIndex(index) ? '' : 'truncate whitespace-nowrap'"
      >
        <button
          v-if="isSortableHeaderIndex(index)"
          type="button"
          class="inline-flex items-center gap-1.5 min-w-0 max-w-full text-left font-semibold text-gray-700 rounded px-1 py-0.5 -mx-1 hover:bg-gray-200/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-main cursor-pointer"
          :aria-sort="
            sortColumnKey === tableKeys[index]
              ? sortDirection === 'asc'
                ? 'ascending'
                : 'descending'
              : 'none'
          "
          :aria-label="sortAriaLabel(header, index)"
          @click="emitSortColumn(tableKeys[index])"
        >
          <span class="truncate">{{ header }}</span>
          <Icon
            v-if="sortColumnKey === tableKeys[index]"
            :icon="sortDirection === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'"
            class="shrink-0 w-4 h-4 text-main"
            aria-hidden="true"
          />
        </button>
        <span v-else class="truncate whitespace-nowrap">{{ header }}</span>
      </div>
    </div>
    <div
      v-for="item in paginatedItems"
      :key="item.id"
      :item-id="item.id"
      class="AdminTable__row grid items-stretch h-14 pl-2.5 hover:bg-gray-50 transition-colors min-w-125"
      :class="{
        'bg-yellow-50 hover:bg-yellow-100': isOverlayShownFunction
          ? isOverlayShownFunction(item)
          : false,
      }"
      :style="{ gridTemplateColumns: columnWidths?.join(' ') || defaultColumnWidths.join(' ') }"
    >
      <div v-if="!!$slots.adminTableItemFirst" class="h-full flex items-center overflow-hidden truncate">
        <slot name="adminTableItemFirst" :item="item"></slot>
      </div>

      <div
        v-for="(tableKey, index) in tableKeys"
        :key="index"
        class="h-full flex items-center overflow-hidden pr-4 min-w-0"
        :class="[
          noWrapTruncateKeys?.includes(tableKey)
            ? 'whitespace-nowrap'
            : noWrapKeys?.includes(tableKey)
              ? 'whitespace-nowrap'
              : 'truncate whitespace-nowrap',
        ]"
      >
        <img
          loading="lazy"
          v-if="index === 0 && withLogo && getNestedObjectValue(item, logoField)"
          :src="getNestedObjectValue(item, logoField)"
          class="w-8 h-8 object-cover mr-3 rounded shrink-0"
        />

        <template v-if="$slots[`cell-${tableKey}`]">
          <slot :name="`cell-${tableKey}`" :item="item" :key="tableKey"></slot>
        </template>
        <span
          v-else
          class="text-sm px-3 overflow-hidden"
          :class="[
            noWrapTruncateKeys?.includes(tableKey)
              ? 'whitespace-nowrap truncate min-w-0'
              : noWrapKeys?.includes(tableKey)
                ? 'whitespace-nowrap shrink-0'
                : 'truncate',
          ]"
          :title="getContent(item, tableKey, true)"
        >
          {{ getContent(item, tableKey, Boolean(noWrapKeys?.includes(tableKey) || noWrapTruncateKeys?.includes(tableKey))) }}
        </span>
      </div>

      <div v-if="!!$slots.editContentCell" class="h-full flex items-center justify-end pr-2.5">
        <slot name="editContentCell" :item="item"></slot>
      </div>
    </div>

    <div class="mt-4">
      <Pagination :items="items" v-model="paginatedItems" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Pagination from '@/components/global/Pagination.vue'
import { getNestedObjectValue, reduceText } from '@/services/utils/UtilsService'
import type { SortableEvent } from 'sortablejs'
import { Icon } from '@iconify/vue'
import { onMounted, ref, watch, type Ref } from 'vue'
import { useDraggable } from 'vue-draggable-plus'
import { useI18n } from 'vue-i18n'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Item = any

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    items: Item[]
    tableKeys: string[]
    columnWidths?: string[]
    headers?: string[]
    plainText?: boolean
    isDraggable?: boolean
    withLogo?: boolean
    logoField?: string
    isOverlayShownFunction?: (item: Item) => boolean
    /** Keys for which cell content should not wrap (e.g. title column). */
    noWrapKeys?: string[]
    /** Keys for which cell is one line + truncate to fit (e.g. description column). */
    noWrapTruncateKeys?: string[]
    /** Subset of `tableKeys` that show sort controls in the header row. */
    sortableTableKeys?: string[]
    sortColumnKey?: string | null
    sortDirection?: 'asc' | 'desc'
  }>(),
  {
    sortColumnKey: null,
    sortDirection: 'asc',
  },
)

const emit = defineEmits<{
  sortColumn: [key: string]
  'update:order': [
    payload: {
      id: string | null
      oldIndex: number | undefined
      newIndex: number | undefined
    },
  ]
}>()

function isSortableHeaderIndex(headerIndex: number): boolean {
  if (
    headerIndex >= props.tableKeys.length ||
    !props.sortableTableKeys?.length ||
    !props.headers?.length
  ) {
    return false
  }
  const key = props.tableKeys[headerIndex]
  return props.sortableTableKeys.includes(key)
}

function emitSortColumn(key: string): void {
  emit('sortColumn', key)
}

function sortAriaLabel(header: string, headerIndex: number): string {
  const key = props.tableKeys[headerIndex]
  const isActive = props.sortColumnKey === key
  if (!isActive) {
    return t('admin.table.sortColumnInactive', { column: header })
  }
  return props.sortDirection === 'asc'
    ? t('admin.table.sortColumnAsc', { column: header })
    : t('admin.table.sortColumnDesc', { column: header })
}

const defaultColumnWidths = ['15%', '40%', '25%', '20%']

const paginatedItems: Ref<Item[]> = ref([])

function isStringDate(candidate: string): boolean {
  const date = new Date(candidate)
  return !isNaN(date.getTime()) && candidate.length > 5
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getContent = (item: any, key: string, isFullText: boolean) => {
  const val = getNestedObjectValue(item, key)
  if (!val) return ''

  if (isStringDate(val)) {
    return new Date(val).toLocaleDateString()
  }

  return isFullText ? val : reduceText(val, 25)
}

const el = ref<HTMLElement | null>(null)
onMounted(() => {
  initDraggable()
})

watch(
  () => paginatedItems.value,
  () => initDraggable(),
)

const initDraggable = () => {
  if (props.isDraggable) {
    useDraggable(el, paginatedItems, {
      animation: 150,
      handle: '.AdminTable__row',
      onUpdate(e: SortableEvent) {
        emit('update:order', {
          id: e.item.getAttribute('item-id'),
          oldIndex: e.oldIndex,
          newIndex: e.newIndex,
        })
      },
    })
  }
}
</script>

<style scoped>
.AdminTable__row > div {
  background-color: inherit;
  border-bottom: 1px solid rgb(229 231 235);
}
</style>
